import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ManklarLogoHorizontal } from '../ManklarLogo';
import { PortalSelector, PortalIndicator } from '../PortalSelector';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Gift,
  BarChart3,
  LogOut,
  ChevronRight,
  Search,
  Menu,
  X,
  HelpCircle,
  FileText,
  Droplets,
  Wrench,
  UserCog,
  Settings
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

// Sidebar Navigation Item
function NavItem({ to, icon: Icon, label, badge, active, onClick }) {
  return (
    <Link to={to} onClick={onClick}>
      <div
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
          ${active 
            ? 'bg-white/10 text-white' 
            : 'text-zinc-400 hover:text-white hover:bg-white/5'
          }
        `}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className="text-sm font-medium flex-1">{label}</span>
        {badge && (
          <span className="px-2 py-0.5 text-xs font-medium bg-white text-black rounded-full">
            {badge}
          </span>
        )}
        {active && <ChevronRight className="w-4 h-4 text-zinc-500 hidden sm:block" />}
      </div>
    </Link>
  );
}

// Admin Sidebar Component - Now responsive
export function AdminSidebar({ pendingRewards = 0, pendingLeads = 0, pendingTickets = 0, onLogout, isOpen, onClose }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const navItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { to: '/admin/leads', icon: FileText, label: 'Solicitudes', badge: pendingLeads > 0 ? pendingLeads : null },
    { to: '/admin/tickets', icon: Wrench, label: 'Gestión Técnica', badge: pendingTickets > 0 ? pendingTickets : null },
    { to: '/admin/installations', icon: Droplets, label: 'Instalaciones' },
    { to: '/admin/clients', icon: Users, label: 'Clientes' },
    { to: '/admin/referrals', icon: UserPlus, label: 'Referidos' },
    { to: '/admin/rewards', icon: Gift, label: 'Bonificaciones', badge: pendingRewards > 0 ? pendingRewards : null },
    { to: '/admin/analytics', icon: BarChart3, label: 'Analíticas' },
    { to: '/admin/usuarios', icon: UserCog, label: 'Usuarios' },
    { to: '/admin/configuracion', icon: Settings, label: 'Configuración' },
  ];

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      onClose?.();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-screen bg-[#050505] border-r border-white/10 
          flex flex-col z-50
          w-[280px] lg:w-64
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo + Close Button (mobile) */}
        <div className="p-4 lg:p-6 border-b border-white/10 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3" onClick={handleNavClick}>
            <ManklarLogoHorizontal size="default" color="#A1A1AA" />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-zinc-400 hover:text-white"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="px-4 lg:px-6 py-2">
          <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-600 font-medium">
            Centro de Control
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 lg:p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = item.exact 
              ? currentPath === item.to 
              : currentPath.startsWith(item.to);
            return (
              <NavItem
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.label}
                badge={item.badge}
                active={isActive}
                onClick={handleNavClick}
              />
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="p-3 lg:p-4 border-t border-white/10 space-y-3">
          {/* Portal Selector */}
          <PortalSelector variant="full" showDescription={false} />
          
          <div className="pt-2 border-t border-white/[0.06]">
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

// Admin Header Component - Now responsive with hamburger menu
export function AdminHeader({ title, subtitle, onSearch, searchPlaceholder = 'Buscar...', onMenuClick }) {
  return (
    <header className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur-lg border-b border-white/10">
      <div className="px-4 lg:px-8 py-3 lg:py-4 flex items-center justify-between gap-4">
        {/* Left side: Menu button + Title */}
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden flex-shrink-0 text-zinc-400 hover:text-white hover:bg-white/10"
            onClick={onMenuClick}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg lg:text-2xl font-light text-white font-['Outfit'] tracking-tight truncate">
              {title}
            </h1>
            {subtitle && (
              <p className="text-xs lg:text-sm text-zinc-500 mt-0.5 truncate hidden sm:block">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Right side: Search */}
        {onSearch && (
          <div className="relative hidden sm:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              placeholder={searchPlaceholder}
              onChange={(e) => onSearch(e.target.value)}
              className="w-48 lg:w-64 pl-10 h-9 lg:h-10 bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/30 rounded-lg text-sm"
              data-testid="global-search-input"
            />
          </div>
        )}
      </div>
      
      {/* Mobile Search - Full width below header */}
      {onSearch && (
        <div className="px-4 pb-3 sm:hidden">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              placeholder={searchPlaceholder}
              onChange={(e) => onSearch(e.target.value)}
              className="w-full pl-10 h-10 bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/30 rounded-lg text-sm"
              data-testid="mobile-search-input"
            />
          </div>
        </div>
      )}
    </header>
  );
}

// Admin Layout Wrapper - Responsive
export function AdminLayout({ children, title, subtitle, pendingRewards, onLogout, onSearch }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  return (
    <div className="min-h-screen bg-[#050505] overflow-x-hidden">
      <AdminSidebar 
        pendingRewards={pendingRewards} 
        onLogout={onLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="lg:ml-64 min-h-screen">
        <AdminHeader 
          title={title} 
          subtitle={subtitle} 
          onSearch={onSearch}
          onMenuClick={() => setSidebarOpen(true)}
        />
        <main className="p-4 lg:p-8 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
